import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { Users, Plus, Trash2, UserPlus, UserMinus, Crown, Edit2, X } from 'lucide-react';
import PageTemplate from './PageTemplate';
import { UnifiedHeader } from './UnifiedHeader';
import API_BASE_URL from '../config';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/groupsTab.css';
import FloatingActionBar from './FloatingActionBar';

interface SharingGroup {
  id: number;
  name: string;
  created_at: string;
  member_count: number;
  my_role: 'owner' | 'admin' | 'editor' | 'viewer';
  item_count: number;
  shared_collections: Array<{ id: number; name: string }>;
}

interface GroupMember {
  id: number;
  email: string;
  display_name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  joined_at: string;
}

interface GroupsTabProps {
  isActive?: boolean;
  onNavigateToCollection?: (collectionId: number) => void;
  onNavigateToInventory?: (groupId: number) => void;
  onFontSizeToggle?: () => void;
  fontSize?: string;
  showHeader?: boolean;
  onOpenAppContext?: () => void;
}

const GroupsTab: React.FC<GroupsTabProps> = ({ 
  isActive = true, 
  onNavigateToCollection, 
  onNavigateToInventory, 
  onFontSizeToggle, 
  fontSize, 
  showHeader = true,
  onOpenAppContext
}) => {
  const { token, user } = useAuth();
  const { activeSpace } = useSpace();
  const [groups, setGroups] = useState<SharingGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<SharingGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editGroupName, setEditGroupName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [error, setError] = useState<string | null>(null);

  // Fetch groups
  const fetchGroups = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  // Fetch group members
  const fetchMembers = async (groupId: number) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  // Fetch groups when tab becomes active or token changes
  useEffect(() => {
    if (isActive) {
      fetchGroups();
    }
  }, [token, isActive]);

  useEffect(() => {
    if (selectedGroup) {
      fetchMembers(selectedGroup.id);
    }
  }, [selectedGroup]);

  // Create new group
  const handleCreateGroup = async () => {
    if (!token || !newGroupName.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newGroupName.trim() })
      });
      
      if (response.ok) {
        setNewGroupName('');
        setShowCreateModal(false);
        await fetchGroups();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create group');
      }
    } catch (err) {
      console.error('Failed to create group:', err);
      setError('Failed to create group');
    }
  };

  // Add member to group
  const handleAddMember = async () => {
    if (!token || !selectedGroup || !newMemberEmail.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${selectedGroup.id}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newMemberEmail.trim(),
          role: newMemberRole
        })
      });
      
      if (response.ok) {
        setNewMemberEmail('');
        setNewMemberRole('editor');
        setShowAddMemberModal(false);
        await fetchMembers(selectedGroup.id);
        await fetchGroups(); // Refresh to update member count
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add member');
      }
    } catch (err) {
      console.error('Failed to add member:', err);
      setError('Failed to add member');
    }
  };

  // Remove member from group
  const handleRemoveMember = async (memberId: number) => {
    if (!token || !selectedGroup) return;
    
    if (!window.confirm('Remove this member from the group?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${selectedGroup.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchMembers(selectedGroup.id);
        await fetchGroups(); // Refresh to update member count
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove member');
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError('Failed to remove member');
    }
  };

  // Update group name
  const handleUpdateGroupName = async () => {
    if (!token || !selectedGroup || !editGroupName.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${selectedGroup.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: editGroupName.trim() })
      });
      
      if (response.ok) {
        await fetchGroups();
        setSelectedGroup({ ...selectedGroup, name: editGroupName.trim() });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update group name');
      }
    } catch (err) {
      console.error('Failed to update group name:', err);
      setError('Failed to update group name');
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId: number) => {
    if (!token) return;
    
    if (!window.confirm('Delete this group? All sharing will be removed.')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${groupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setShowGroupModal(false);
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
          setMembers([]);
        }
        await fetchGroups();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete group');
      }
    } catch (err) {
      console.error('Failed to delete group:', err);
      setError('Failed to delete group');
    }
  };

  const canManageGroup = (group: SharingGroup) => {
    return group.my_role === 'owner' || group.my_role === 'admin';
  };

  // Extract menu bar for UnifiedHeader
  const groupsMenuBar = (
    <div className="transparent-menu-bar groups-menu-bar">
      <button className="menu-tab-btn menu-tab-btn-primary" onClick={() => setShowCreateModal(true)}>
        <Plus size={18} />
        <span>Create</span>
      </button>
    </div>
  );

  return (
    <>
      {isActive && showHeader && (
        <UnifiedHeader 
          title="People"
          subtitle="Friends and Relations"
          menuBar={groupsMenuBar}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      <PageTemplate
        showPageHeader={false}
        showInputArea={false}
        hasGrayBackground={false}
        enableSmartScroll={false}
        className="groups-page"
      >
      {loading ? (
        <div className="groups-loading">Loading groups...</div>
      ) : (
        <>
          {error && (
            <div className="groups-error">
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {groups.length === 0 ? (
            <div className="groups-empty">
              <Users size={48} />
              <p>No groups yet</p>
              <p className="groups-empty-hint">Create a group to start sharing</p>
            </div>
          ) : (
            <>
              {groups.map(group => (
                <div
                  key={group.id}
                  className={`group-item ${selectedGroup?.id === group.id ? 'active' : ''}`}
                >
                  <div 
                    className="group-item-main"
                    onClick={async () => {
                      console.log('Card clicked:', group.name);
                      setSelectedGroup(group);
                      setEditGroupName(group.name);
                      // Fetch members first
                      if (token) {
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/sharing/groups/${group.id}/members`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setMembers(data.members || []);
                          }
                        } catch (err) {
                          console.error('Failed to fetch members:', err);
                        }
                      }
                      setShowGroupModal(true);
                      console.log('Modal should show now');
                    }}
                  >
                    <div className="group-item-header">
                      <div className="group-item-icon">
                        <Users size={18} />
                      </div>
                      <div className="group-item-name">{group.name}</div>
                    </div>
                    <div className="group-item-stats">
                      <span>{group.member_count} {group.member_count === 1 ? 'member' : 'members'}</span>
                      <span>•</span>
                      <span>{group.my_role}</span>
                    </div>
                    {group.item_count > 0 && (
                      <button
                        className="group-item-stats group-shared-items-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onNavigateToInventory) {
                            onNavigateToInventory(group.id);
                          }
                        }}
                      >
                        {group.item_count} shared {group.item_count === 1 ? 'item' : 'items'}
                      </button>
                    )}
                  </div>
                  {group.shared_collections && group.shared_collections.length > 0 && (
                    <div className="group-item-collections">
                      {group.shared_collections.map(collection => (
                        <button
                          key={collection.id}
                          className="collection-tag"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onNavigateToCollection) {
                              onNavigateToCollection(collection.id);
                            } else {
                              console.log('Navigate to collection:', collection.id, collection.name);
                            }
                          }}
                          title={`View ${collection.name} collection`}
                        >
                          {collection.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Group</h3>
              <ModalCloseButton onClick={() => setShowCreateModal(false)} />
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., Family, Roommates, Team"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim()}
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Member to {selectedGroup.name}</h3>
              <ModalCloseButton onClick={() => setShowAddMemberModal(false)} />
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="member@example.com"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value as 'admin' | 'editor' | 'viewer')}
                >
                  <option value="viewer">Viewer - Can view shared items</option>
                  <option value="editor">Editor - Can add and edit items</option>
                  <option value="admin">Admin - Can manage group members</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddMemberModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleAddMember}
                disabled={!newMemberEmail.trim()}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

    </PageTemplate>

      {/* Floating Create Group button when header is hidden (embedded in PeopleView) */}
      {!showHeader && (
        <FloatingActionBar>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '5px 10px',
              background: 'transparent',
              color: 'var(--color-accent-secondary)',
              border: 'none',
              borderRadius: '14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            title="Create group"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add
          </button>
        </FloatingActionBar>
      )}

      {/* Group Management Modal - Rendered via portal to appear above unified header */}
      {showGroupModal && selectedGroup && ReactDOM.createPortal(
        <div className="group-manage-modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="group-manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="group-manage-modal-header">
              <h2>Manage Group</h2>
              <ModalCloseButton onClick={() => setShowGroupModal(false)} />
            </div>
            <div className="group-manage-modal-body">
              {/* Group Name */}
              <div className="group-manage-section">
                <label className="group-manage-label">Group Name</label>
                <div className="group-manage-name-row">
                  <input
                    type="text"
                    value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                    placeholder="Group name"
                    className="group-manage-input"
                  />
                  <button
                    className="group-manage-update-btn"
                    onClick={handleUpdateGroupName}
                    disabled={!editGroupName.trim() || editGroupName === selectedGroup.name}
                  >
                    Update
                  </button>
                </div>
              </div>

              {/* Members List - Scrollable */}
              <div className="group-manage-section">
                <label className="group-manage-label">Members ({members.length})</label>
                {members.length === 0 ? (
                  <p className="group-manage-loading">Loading members...</p>
                ) : (
                  <div className="group-manage-members-list">
                    {members.map(member => (
                      <div key={member.id} className="group-manage-member-item">
                        <div className="group-manage-member-info">
                          <div className="group-manage-member-name">
                            {member.display_name || member.email}
                            {member.role === 'owner' && <Crown size={12} className="group-manage-crown" />}
                          </div>
                          <div className="group-manage-member-email">{member.email}</div>
                        </div>
                        <div className="group-manage-member-role">{member.role}</div>
                        {canManageGroup(selectedGroup) && member.role !== 'owner' && (
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="group-manage-remove-btn"
                            title="Remove member"
                          >
                            <UserMinus size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Actions */}
            {canManageGroup(selectedGroup) && (
              <div className="group-manage-modal-footer">
                <button
                  className="group-manage-btn-secondary"
                  onClick={() => {
                    setShowGroupModal(false);
                    setShowAddMemberModal(true);
                  }}
                >
                  <UserPlus size={16} />
                  Add Member
                </button>
                {selectedGroup.my_role === 'owner' && (
                  <button
                    className="group-manage-btn-danger"
                    onClick={() => handleDeleteGroup(selectedGroup.id)}
                  >
                    <Trash2 size={16} />
                    Delete Group
                  </button>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GroupsTab;
